import { LoyaltyTier, TIER_STYLES } from '@/constants/loyalty'
import { RD } from '@/constants/redesign'
import CloseIcon from '@mui/icons-material/Close'
import { Box, IconButton, Stack, Typography } from '@mui/material'
import { useEffect, useState } from 'react'

export type LoyaltyToastVariant =
  | 'standard'
  | 'cross-branch'
  | 'pending'
  | 'error'
  | 'tier-up'
  | 'bulk'

export type LoyaltyActivityType = 'CHECKIN' | 'CLASS' | 'PT' | 'SWIMMING' | 'PURCHASE'

export interface LoyaltyToastPayload {
  id: string
  variant: LoyaltyToastVariant
  /** Member name (or bulk summary label for the bulk variant). */
  memberName: string
  /** Sub-line — activity description (e.g. "Club Check-in · Home"). */
  activity: string
  /** Activity category — drives the left edge color bar. */
  activityType: LoyaltyActivityType
  /** Points awarded — positive integer. */
  pointsDelta: number
  /** Running balance after this award (optional). */
  newBalance?: number
  /** Member's current tier — drives the footer chip. */
  tier?: LoyaltyTier
  /** Progress to next tier (0..1) — footer progress bar. */
  progressToNext?: number
  /** Reason / context for pending or error variants. */
  reason?: string
  /** Branch name — surfaced as "Cross-branch" pill when set. */
  branch?: string
  /** Pre-computed initials for the avatar; falls back to memberName. */
  initials?: string
}

interface LoyaltyPointsToastProps {
  payload: LoyaltyToastPayload
  /** Called by the user clicking ×, or by the auto-dismiss timer. */
  onDismiss: (id: string) => void
  /** Auto-dismiss delay in ms (matches mockup at 4000). */
  dismissAfterMs?: number
}

/** Category-coded swatches for the 4px left edge bar + small dot. */
const CATEGORY_COLORS: Record<LoyaltyActivityType, string> = {
  CHECKIN: '#2AACE2',
  CLASS: '#DE2826',
  PT: '#F18230',
  SWIMMING: '#FFC82C',
  PURCHASE: '#2E7D32',
}

const VARIANT_BAR: Partial<Record<LoyaltyToastVariant, string>> = {
  pending: RD.warnIcon,
  error: '#B90810',
  'tier-up': '#8A5D00',
}

const VARIANT_POINTS_COLOR: Partial<Record<LoyaltyToastVariant, string>> = {
  standard: '#1F7A2E',
  'cross-branch': '#1F7A2E',
  pending: RD.warnIcon,
  error: '#B90810',
  'tier-up': '#8A5D00',
  bulk: '#1F7A2E',
}

const deriveInitials = (name: string): string =>
  name
    .split(' ')
    .filter(Boolean)
    .map((n) => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase() || '?'

/**
 * QTY-1713 — Loyalty points toast. Top-right anchored, slides in from the
 * edge, auto-dismisses after a fixed window with a thin progress tick along
 * the bottom counting down. Same component is also fired by attendance
 * (QTY-1714) and POS (QTY-1715) once those land — the variant prop covers
 * pending / error / tier-up / bulk paths surfaced by those flows.
 */
export const LoyaltyPointsToast = ({
  payload,
  onDismiss,
  dismissAfterMs = 4000,
}: LoyaltyPointsToastProps) => {
  const [enter, setEnter] = useState(false)
  const [leave, setLeave] = useState(false)

  // Slide-in: schedule one frame after mount so the transform transition runs.
  useEffect(() => {
    const id = window.requestAnimationFrame(() => setEnter(true))
    return () => window.cancelAnimationFrame(id)
  }, [])

  // Auto-dismiss after dismissAfterMs — the slide-out + onDismiss are split
  // so the parent receives the dismissal AFTER the exit animation completes.
  useEffect(() => {
    const exitTimer = window.setTimeout(() => setLeave(true), dismissAfterMs)
    const removeTimer = window.setTimeout(() => onDismiss(payload.id), dismissAfterMs + 320)
    return () => {
      window.clearTimeout(exitTimer)
      window.clearTimeout(removeTimer)
    }
  }, [payload.id, onDismiss, dismissAfterMs])

  const handleManualClose = () => {
    setLeave(true)
    window.setTimeout(() => onDismiss(payload.id), 320)
  }

  const barColor = VARIANT_BAR[payload.variant] ?? CATEGORY_COLORS[payload.activityType]
  const swatch = CATEGORY_COLORS[payload.activityType]
  const pointsColor = VARIANT_POINTS_COLOR[payload.variant] ?? '#1F7A2E'
  const tierStyle = payload.tier ? TIER_STYLES[payload.tier] : null
  const isTierUp = payload.variant === 'tier-up'
  const isBulk = payload.variant === 'bulk'
  const initials = payload.initials ?? deriveInitials(payload.memberName)

  return (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        position: 'relative',
        width: 360,
        bgcolor: RD.surface,
        border: `1px solid ${RD.border}`,
        borderRadius: '12px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.08), 0 2px 4px rgba(0,0,0,0.04)',
        p: '14px 16px 14px 20px',
        overflow: 'hidden',
        // Slide animation: starts off-screen right, slides to 0, then back out.
        transform: leave ? 'translateX(110%)' : enter ? 'translateX(0)' : 'translateX(110%)',
        opacity: leave ? 0 : enter ? 1 : 0,
        transition: 'transform 320ms cubic-bezier(0.2, 0.8, 0.2, 1), opacity 320ms ease',
        // 4px left edge bar — category-coded for standard, override per variant.
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 4,
          bgcolor: barColor,
        },
      }}
    >
      {/* Header — category chip / cross-branch / close */}
      <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1 }}>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1,
            py: 0.25,
            borderRadius: 999,
            bgcolor: RD.surfaceAlt,
            color: RD.fgMuted,
            fontSize: 11,
            fontWeight: 600,
          }}
        >
          <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: swatch }} />
          {payload.activityType === 'CHECKIN'
            ? 'Check-in'
            : payload.activityType === 'CLASS'
              ? 'Class'
              : payload.activityType === 'PT'
                ? 'PT'
                : payload.activityType === 'SWIMMING'
                  ? 'Swim'
                  : 'Purchase'}
        </Box>
        {payload.variant === 'cross-branch' && payload.branch && (
          <Box
            sx={{
              fontSize: 11,
              fontWeight: 700,
              px: 1,
              py: 0.25,
              borderRadius: 999,
              bgcolor: '#DDEEF8',
              color: '#1E6FA8',
            }}
          >
            Cross-branch
          </Box>
        )}
        <Box sx={{ flex: 1 }} />
        <IconButton
          aria-label="Dismiss"
          size="small"
          onClick={handleManualClose}
          sx={{ width: 22, height: 22, color: RD.fgMuted }}
        >
          <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Stack>

      {/* Body — avatar + name/activity + points */}
      <Stack direction="row" alignItems="center" gap={1.5}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            bgcolor: isBulk ? RD.primary : RD.primaryListBg,
            color: isBulk ? '#fff' : RD.brandDark,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 700,
            fontSize: isBulk ? 18 : 15,
            flexShrink: 0,
          }}
        >
          {isBulk ? '∑' : initials}
        </Box>
        <Stack sx={{ flex: 1, minWidth: 0 }} spacing={0.25}>
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 700,
              color: RD.fg,
              lineHeight: '18px',
              '&::after': isTierUp ? { content: '" 🎉"' } : {},
            }}
          >
            {payload.memberName}
          </Typography>
          <Typography sx={{ fontSize: 12, color: RD.fgMuted, lineHeight: '16px' }}>
            {payload.activity}
          </Typography>
        </Stack>
        <Typography
          sx={{
            fontSize: payload.variant === 'error' ? 14 : 22,
            lineHeight: '26px',
            fontWeight: 700,
            color: pointsColor,
            letterSpacing: '-0.3px',
            fontVariantNumeric: 'tabular-nums',
            flexShrink: 0,
          }}
        >
          {payload.variant === 'error' ? (payload.reason ?? 'Error') : `+${payload.pointsDelta}`}
        </Typography>
      </Stack>

      {/* Footer — tier chip + newBalance (when tier info present); the
          progress bar additionally needs progressToNext. */}
      {tierStyle && (
        <Stack
          direction="row"
          alignItems="center"
          gap={1.25}
          sx={{
            mt: 1.25,
            pt: 1.25,
            borderTop: `1px dashed ${RD.divider}`,
            fontSize: 12,
          }}
        >
          <Box
            sx={{
              px: 1,
              py: 0.25,
              borderRadius: 999,
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '0.04em',
              bgcolor: tierStyle.bgColor,
              color: tierStyle.textColor,
            }}
          >
            {payload.tier}
          </Box>
          {payload.progressToNext != null && (
            <Box
              sx={{
                flex: 1,
                height: 4,
                borderRadius: 2,
                bgcolor: RD.surfaceAlt,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  height: '100%',
                  width: `${Math.min(100, Math.max(0, payload.progressToNext * 100))}%`,
                  background: `linear-gradient(90deg, ${tierStyle.accentColor}, ${tierStyle.fillColor})`,
                }}
              />
            </Box>
          )}
          {payload.newBalance != null && (
            <Typography
              sx={{
                fontSize: 12,
                color: RD.fgMuted,
                fontVariantNumeric: 'tabular-nums',
                flexShrink: 0,
                // Keeps the balance right-aligned when the progress bar is
                // absent (no-op while the flex-1 bar is present).
                ml: 'auto',
              }}
            >
              {payload.newBalance.toLocaleString()} pts
            </Typography>
          )}
        </Stack>
      )}

      {/* Reason line — used by pending variant when no tier info is shown */}
      {payload.reason && payload.variant !== 'error' && (
        <Typography sx={{ fontSize: 12, color: RD.fgMuted, mt: 1, lineHeight: '16px' }}>
          {payload.reason}
        </Typography>
      )}

      {/* Bottom progress tick — scales from 100% → 0% over dismissAfterMs */}
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          height: 2,
          bgcolor: barColor,
          transformOrigin: 'left',
          animation: `loyalty-toast-tick ${dismissAfterMs}ms linear forwards`,
          '@keyframes loyalty-toast-tick': {
            from: { transform: 'scaleX(1)' },
            to: { transform: 'scaleX(0)' },
          },
        }}
      />
    </Box>
  )
}
